import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePublicBranding } from "@/hooks/usePublicBranding";
import { ArrowRight } from "lucide-react";

const HERO_IMAGE = "https://customer-assets-eiarnc6j.emergentagent.net/job_route-registry-3/artifacts/hds4l82r_1fa88561-08f7-4e20-a930-2f970d584f38.jpeg";

export default function Login() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const branding = usePublicBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) nav("/");
  }

  const name = branding.app_name || "Maan group";

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden md:block md:w-1/2 relative"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="login-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="text-xs uppercase tracking-[0.3em] text-white/80">Route Registry · Dispatch Console</div>
          <div className="font-heading text-3xl font-semibold mt-2" data-testid="login-brand-name">{name}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="md:hidden mb-6">
            <img src={HERO_IMAGE} alt={name} className="w-full h-40 object-cover rounded-md" />
          </div>

          <h2 className="font-heading text-3xl font-semibold">Sign in</h2>
          <p className="text-sm text-slate-500 mt-2">Access your {name} dispatch dashboard.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>
          {error && <p data-testid="login-error" className="mt-4 text-sm text-red-600">{error}</p>}
          <button
            data-testid="login-submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60"
          >
            {loading ? "Signing in..." : (<>Continue <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <p className="mt-6 text-sm text-slate-500 text-center">
            No account? <Link to="/register" data-testid="link-register" className="text-[#2563EB] hover:underline">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
